import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import { makeStyles } from 'tss-react/mui';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Avatar, IconButton, useMediaQuery, useTheme } from '@mui/material';
import ErrorHandler, { SidebarIcon } from '../../Utils/handler';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { useConfirm } from 'material-ui-confirm';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import sessionStoreUtil from '../../ApiSetup/Localstorage';
import { useDispatch } from 'react-redux';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import Assets from "../Common/Assets";
import { getAvailableAccess } from '../../Redux/Actions/action';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import TextLabel from '../Common/CommonTextLabel';
import { BucketURL } from '../../ApiSetup/Api';
import MobileBottomNav, { MOBILE_BOTTOM_NAV_HEIGHT } from '../Common/MobileBottomNav';
import MobileDrawerMenu from '../Common/MobileDrawerMenu';
import AdminMobileSidebar from '../Common/AdminMobileSidebar';
import GlobalHeaderSearch from '../Common/GlobalHeaderSearch';

// Dynamic icon mapping - maps icon identifiers to image paths (legacy - kept for backward compatibility)
const ICON_MAP = {
    dashboard: "images/dashboardHigh.svg",
    addMember: "images/addMemberr.svg",
    website: "images/elements.svg",
    memberRequest: "images/memberRequest.svg",
    documents: "images/documents.svg",
    claims: "images/claims.svg",
    memberSearch: "images/membersearch.svg",
    viewMember: "images/membersearch.svg",
    paymentRequest: "images/allTransaction.svg",
    notifications: "images/notifications.svg",
    helpDesk: "images/helpDesk.svg",
    reports: "images/reports.svg",
    settings: "images/settings.svg",
    transactionHistory: "images/transactionHistory.svg",
    allTransaction: "images/allTransaction.svg",
    reminders: "images/notifications.svg",
    expenses: "images/allTransaction.svg",
    postGenerator: "images/elements.svg",
    default: "images/defaultIcon.svg",
};

// MUI Icon component mapping for unique icons per page
const PAGE_MUI_ICONS = {
    "Dashboard": DashboardOutlinedIcon,
    "In Progress": HourglassEmptyOutlinedIcon,
    "Pending Approval": PendingActionsOutlinedIcon,
    "All Members": GroupOutlinedIcon,
    "Active Members": GroupOutlinedIcon,
    "Add Member": PersonAddAltOutlinedIcon,
    "Organisation Management": BusinessOutlinedIcon,
    "Organisation Members": BadgeOutlinedIcon,
    "Organisation Payment": PaymentsOutlinedIcon,
    "All Transaction": AccountBalanceWalletOutlinedIcon,
};

// Fallback icon mapping based on page name (used when API doesn't return icon)
const PAGE_NAME_TO_ICON = {
    "Dashboard": "dashboard",
    "Add Member": "addMember",
    "Website": "website",
    "Member Requests": "memberRequest",
    "Documents": "documents",
    "Document Status Wise": "documents",
    "Claims": "claims",
    "Member Search": "memberSearch",
    "Active Members": "viewMember",
    "Reference Tracking": "memberSearch",
    "Help Desk": "helpDesk",
    "Reports": "reports",
    "Settings": "settings",
    "Transaction History": "transactionHistory",
    "All Transaction": "allTransaction",
    "Organisation Management": "settings",
    "Organisation Members": "viewMember",
    "Organisation Payment": "paymentRequest",
    "Reminders": "reminders",
    "Renewal Reminders": "reminders",
    "Expenses": "expenses",
    "Expense Management": "expenses",
    "Expense Dashboard": "expenses",
    "Expense Categories": "expenses",
    "Expense Reports": "expenses",
    "Post Generator": "postGenerator",
    "Post Categories": "postGenerator",
    "Post Templates": "postGenerator",
};

// Fallback section mapping based on page name (used when API doesn't return section)
const PAGE_NAME_TO_SECTION = {
    // DASHBOARD
    "Dashboard": "DASHBOARD",
    // REGISTRATION JOURNEY (follows user flow)
    "New Registrations": "REGISTRATION",
    "Member Requests": "REGISTRATION",
    "In Progress": "REGISTRATION",
    "Documents": "REGISTRATION",
    "Document Status Wise": "REGISTRATION",
    "Pending Approval": "REGISTRATION",
    // MEMBERS
    "All Members": "MEMBERS",
    "Active Members": "MEMBERS",
    "Member Search": "MEMBERS",
    "Add Member": "MEMBERS",
    "Member Details": "MEMBERS",
    "Reference Tracking": "MEMBERS",
    // ORGANISATION
    "Organisation Management": "ORGANISATION",
    "Organisation Members": "ORGANISATION",
    "Organisation Payment": "ORGANISATION",
    // FINANCE
    "Transaction History": "FINANCE",
    "All Transaction": "FINANCE",
    "All Transactions": "FINANCE",
    // CLAIMS
    "Claims": "CLAIMS",
    // SUPPORT
    "Help Desk": "SUPPORT",
    "Communication": "SUPPORT",
    "Reminders": "SUPPORT",
    "Renewal Reminders": "SUPPORT",
    // EXPENSES (Office Use)
    "Expenses": "EXPENSES",
    "Expense Management": "EXPENSES",
    "Expense Dashboard": "EXPENSES",
    "Expense Categories": "EXPENSES",
    "Expense Reports": "EXPENSES",
    // SETTINGS
    "Website": "SETTINGS",
    "Reports": "SETTINGS",
    "Settings": "SETTINGS",
    // POST GENERATOR
    "Post Generator": "POST_GENERATOR",
    "Post Categories": "POST_GENERATOR",
    "Post Templates": "POST_GENERATOR",
};

// Sidebar sections order - matches user journey
const SECTION_ORDER = ["DASHBOARD", "REGISTRATION", "MEMBERS", "ORGANISATION", "FINANCE", "EXPENSES", "CLAIMS", "SUPPORT", "POST_GENERATOR", "SETTINGS"];

// Section display names (for UI)
const SECTION_LABELS = {
    "DASHBOARD": "Dashboard",
    "REGISTRATION": "Registration Journey",
    "MEMBERS": "Member Management", 
    "ORGANISATION": "Organisation",
    "FINANCE": "Finance",
    "EXPENSES": "Office Expenses",
    "CLAIMS": "Claims",
    "SUPPORT": "Support",
    "POST_GENERATOR": "Post Generator",
    "SETTINGS": "Settings & Config",
};

const drawerWidth = 240;
const collapsedWidth = 72;

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: collapsedWidth,
    // Must keep display:block — spreading this object replaces the Drawer's
    // [up('sm')]: { display:'block' } and otherwise leaves display:none.
    [theme.breakpoints.up('sm')]: {
        width: collapsedWidth,
        display: 'block',
    },
});

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: "#ffffff !important",
    backgroundImage: "none !important",
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: collapsedWidth,
    width: `calc(100% - ${collapsedWidth}px)`,
    [theme.breakpoints.down('sm')]: {
        marginLeft: 0,
        width: '100%',
    },
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
            width: '100%',
        },
    }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
        ...(open && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme),
        }),
        ...(!open && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': closedMixin(theme),
        }),
    }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 3),
    justifyContent: 'flex-end',
    ...theme.mixins.toolbar,
}));

const useStyles = makeStyles()((theme) => {
    return {
        non_active_tab: {
            "& .MuiTypography-root ": {
                fontSize: "16px",
                lineHeight: "24px",
                fontWeight: 400,
            }

        },
        active_tab: {
            "& .MuiTypography-root ": {
                color: "black",
                fontSize: "17px",
                lineHeight: "24.76px",
                fontWeight: 700,
            }
        },
        SideMainMenu: {
            color: "#94a3b8",
            fontWeight: 600,
            fontSize: "10px",
            marginLeft: "8px",
            letterSpacing: "0.5px",
            marginBottom: "6px",
            lineHeight: "14px",
            textTransform: "uppercase",
        }
    }
})


function Layout({ children }) {
    // ---------- HOOK --------- 
    const theme = useTheme();
    const navigate = useNavigate()
    const dispatch = useDispatch();
    const location = useLocation()
    const { classes } = useStyles();
    const confirmDialogue = useConfirm();
    const user = sessionStoreUtil.getLSItem('userData') ? JSON.parse(sessionStoreUtil.getLSItem('userData')) : {}

    // --------- STATE -----------
    const [open, setOpen] = React.useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
    const [businesEditdata, setBusinesEditdata] = React.useState({
        firstName: "",
        mobilenumber: "",
        email: "",
    });
    // const [availableAccess, setAvailableAccess] = React.useState()
    const [anchorEl, setAnchorEl] = React.useState(null);
    const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
    const availableAccess = useSelector(value => value?.allAvailableAccess?.AllAvailableAccessList)

    const viewedPageAccess = availableAccess?.filter((e) => e?.actionsType?.includes("View") && e?.isVisible !== false)
    const openMenu = Boolean(anchorEl);

    // Group pages by section and sort by displayOrder
    const groupedPages = React.useMemo(() => {
        if (!viewedPageAccess) return {};
        
        const grouped = {};
        SECTION_ORDER.forEach(section => {
            grouped[section] = [];
        });
        
        viewedPageAccess.forEach(page => {
            // Use API section, fallback to page name mapping, then default to OTHER
            const section = page.section || PAGE_NAME_TO_SECTION[page.pageName] || "OTHER";
            if (!grouped[section]) grouped[section] = [];
            grouped[section].push(page);
        });
        
        // Sort each section by displayOrder
        Object.keys(grouped).forEach(section => {
            grouped[section].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
        });
        
        return grouped;
    }, [viewedPageAccess]);

    // Get icon path for a menu item
    const getIconPath = (menu) => {
        // First try the icon from API, then fallback to page name mapping
        const iconKey = menu?.icon || PAGE_NAME_TO_ICON[menu?.pageName] || "default";
        return ICON_MAP[iconKey] || ICON_MAP.default;
    };

    // Helper to prefix paths with /admin
    const getAdminPath = (path) => {
        if (!path) return '/admin';
        if (path.startsWith('/admin')) return path;
        return `/admin${path}`;
    }

    // const activePage = "Dashboard"
    const activePage = viewedPageAccess?.find((e) => getAdminPath(e.path) === location?.pathname)?.pageName

    // --------- HANDLE CHANGE ----------------
    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const onchangeBusinesdata = (e) => {
        const { name, value } = e.target
        setBusinesEditdata({ ...businesEditdata, [name]: value });
    }

    const handleEditClick = () => {
        document.getElementById('profilePicInput').click();
    };

    const handleLogout = () => {
        dispatch(showLoader(true));
        setTimeout(() => {
            sessionStoreUtil.removeLSItem("auth_token")
            navigate('/admin/login')
            dispatch(showLoader(false));
        }, 1000)
    }

    const handleSidebarLogout = () => {
        confirmDialogue({
            title: `Are you sure you want to Log Out?`,
            description: "This action will log you out of your account.",
            confirmationText: (
                <Typography sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px" } }} >
                    Yes, Log Out
                </Typography>
            ),
            cancellationText: (
                <Typography sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px" } }}>
                    NO, Stay Logged In
                </Typography>
            ),
            titleProps: { sx: { fontSize: "18px", fontWeight: 500, color: "#3E4094", padding: "5px 23px", }, },
        })
            .then(() => { handleLogout(); });
    };

    const handleNavigation = (path, index) => {
        navigate(getAdminPath(path));
        if (isMdDown) {
            setOpen(false);
        }
    };

    // ---------- APi ------------ 
    React.useEffect(() => {
        dispatch(showLoader(true));
        dispatch(getAvailableAccess())
            .unwrap() // Automatically handles async response
            .catch((error) => console.error("error", error))
            .finally(() => dispatch(showLoader(false)));
    }, [dispatch]);

    React.useEffect(() => {
        const handleResize = () => {
            if (isMdDown) {
                setOpen(false);
            } else {
                setOpen(true);
            }
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isMdDown]);

    // React.useEffect(() => {
    //     _getRoleList()
    // }, [])

    return (
        <>
            <Box sx={{ display: 'flex', bgcolor: '#f3f3f7', minHeight: '100vh' }}>
                {/* <CssBaseline /> */}

                <AppBar position="fixed" open={open} sx={{ boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }} >
                    <Box display={"flex"} alignItems={"center"} width="100%" px={{ xs: 0, sm: 1 }}>
                        {/* Sidebar toggle — visible on mobile and desktop */}
                        <Tooltip title={open ? 'Collapse menu' : 'Expand menu'} placement="bottom">
                            <IconButton
                                onClick={() => setOpen(!open)}
                                sx={{
                                    display: 'flex',
                                    ml: { xs: 1, sm: 1.5 },
                                    color: '#3E4094',
                                    backgroundColor: '#f1f5f9',
                                    '&:hover': { backgroundColor: '#e2e8f0' },
                                }}
                                aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
                            >
                                {open ? <ChevronLeftIcon /> : <MenuIcon />}
                            </IconButton>
                        </Tooltip>
                        <Toolbar
                            sx={{
                                flex: { xs: 1, sm: '0 0 auto' },
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                                my: "3px",
                                minHeight: { xs: '56px', sm: '64px' },
                                px: { xs: 0.5, sm: 1 },
                                maxWidth: { sm: '38%', md: '32%' },
                            }}
                        >
                            <Typography
                                noWrap
                                component="div"
                                sx={{
                                    fontSize: { xs: "12px", sm: "14px", md: "18px", lg: "22px" },
                                    color: '#2f2481 !important',
                                    fontWeight: "800",
                                }}
                            >
                                પાટીદાર પરિવાર સુરક્ષા કવચ (PPSK)
                            </Typography>
                        </Toolbar>
                        <Box
                            sx={{
                                display: { xs: 'none', sm: 'flex' },
                                flex: 1,
                                justifyContent: 'center',
                                minWidth: 0,
                                px: 1,
                            }}
                        >
                            <GlobalHeaderSearch variant="desktop" />
                        </Box>
                        <Box
                            sx={{
                                display: { xs: 'flex', sm: 'none' },
                                ml: 'auto',
                                mr: 0.5,
                            }}
                        >
                            <GlobalHeaderSearch variant="mobile" />
                        </Box>
                        <Box alignItems={"center"} mr={{ xs: "10px", sm: "20px" }} flexShrink={0}>
                            <Box
                                component="button"
                                type="button"
                                display={"flex"}
                                gap={{ xs: "6px", sm: "10px" }}
                                onClick={handleClick}
                                sx={{
                                    cursor: "pointer",
                                    border: "none",
                                    background: "transparent",
                                    padding: 0,
                                    minHeight: 44,
                                    minWidth: 44,
                                    alignItems: "center",
                                }}
                                aria-label="Open profile menu"
                            >
                                <Box sx={{ display: { xs: "none", sm: "block" } }}>
                                    <Box display={"flex"} flexDirection={"column"} alignItems={'flex-end'}>
                                        <TextLabel width={"max-content"} fontWeight={"500"} fontSize={{ xs: "14px", sm: "16px" }} color={theme.palette.primary.main} title={user?.fullName} />
                                        <TextLabel width={"max-content"} fontWeight={"600"} fontSize={{ xs: "12px", sm: "14px" }} color={theme.palette.primary.main} title={user?.loginRole} />
                                    </Box>
                                </Box>
                                {user?.avtar ? (
                                    <Avatar src={BucketURL + user?.avtar} alt={"image"} sx={{ height: { xs: 40, sm: 40 }, width: { xs: 40, sm: 40 } }} />
                                ) : (
                                    <Avatar sx={{ height: { xs: 40, sm: 40 }, width: { xs: 40, sm: 40 }, fontSize: { xs: "14px", sm: "16px" } }} />
                                )}
                            </Box>
                        </Box>
                    </Box>
                </AppBar >

                {/* Mobile sidebar — separate compact drawer, not the desktop permanent one */}
                <AdminMobileSidebar
                    open={open}
                    onClose={() => setOpen(false)}
                    groupedPages={groupedPages}
                    sectionOrder={SECTION_ORDER}
                    sectionLabels={SECTION_LABELS}
                    activePage={activePage}
                    onNavigate={(path) => handleNavigation(path)}
                    onLogout={handleSidebarLogout}
                />

                <Drawer 
                    variant="permanent" 
                    open={open} 
                    sx={{ 
                        '& .MuiDrawer-paper': { borderRight: '1px solid #e2e8f0' },
                    }}
                >
                    <DrawerHeader sx={{ justifyContent: open ? 'space-between' : 'center', px: open ? 2 : 1 }}>
                        {open ? (
                            <Box display="flex" gap={1} justifyContent="space-between" alignItems="center" width="100%">
                                <Box display={'flex'} alignItems={'center'} sx={{ gap: "10px" }}>
                                    <Assets src={"images/logo.svg"} height={"45px"} width={"45px"} />
                                    <Typography noWrap component="div" sx={{ fontSize: "26px", color: '#2f2481 !important', fontWeight: "600" }}>PPSK</Typography>
                                </Box>
                                <IconButton 
                                    onClick={() => setOpen(false)}
                                    sx={{ 
                                        backgroundColor: '#f1f5f9',
                                        '&:hover': { backgroundColor: '#e2e8f0' },
                                    }}
                                >
                                    <ChevronLeftIcon sx={{ color: "#64748b" }} />
                                </IconButton>
                            </Box>
                        ) : (
                            <Tooltip title="Expand menu" placement="right" arrow>
                                <IconButton 
                                    onClick={() => setOpen(true)}
                                    aria-label="Expand sidebar"
                                    sx={{ 
                                        backgroundColor: '#3E4094',
                                        color: '#fff',
                                        '&:hover': { backgroundColor: '#363785' },
                                    }}
                                >
                                    <ChevronRightIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </DrawerHeader>

                    <List sx={{ mt: "10px", px: 1 }}>
                        {/* Dynamic Sections - Rendered from API data */}
                        {SECTION_ORDER.map((section, sectionIndex) => {
                            const sectionPages = groupedPages[section] || [];
                            if (sectionPages.length === 0) return null;
                            
                            return (
                                <React.Fragment key={section}>
                                    {/* Section Header */}
                                    <Box sx={{ mt: sectionIndex > 0 ? 2 : 0 }}>
                                        {open && (
                                            <Box className={classes.SideMainMenu} sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.2s' }}>
                                                {SECTION_LABELS[section] || section}
                                            </Box>
                                        )}
                                    </Box>
                                    
                                    {/* Section Pages */}
                                    {sectionPages.map((menu, index) => {
                                        const isActive = activePage === menu?.pageName;
                                        const MuiIcon = PAGE_MUI_ICONS[menu?.pageName];
                                        return (
                                            <Tooltip key={menu?.pageName || index} title={!open ? menu?.pageName : ''} placement="right" arrow>
                                                <ListItem disablePadding sx={{ display: "block", mb: 0.25 }}>
                                                    <Link to={getAdminPath(menu?.path)} style={{ textDecoration: "none", color: "inherit" }}>
                                                        <ListItemButton 
                                                            onClick={() => handleNavigation(menu?.path, index)} 
                                                            sx={{ 
                                                                minHeight: 38,
                                                                borderRadius: '8px',
                                                                px: 1.5,
                                                                py: 0.75,
                                                                justifyContent: open ? 'initial' : 'center',
                                                                backgroundColor: isActive ? '#3E4094' : 'transparent',
                                                                '&:hover': {
                                                                    backgroundColor: isActive ? '#363785' : '#f1f5f9',
                                                                },
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ 
                                                                minWidth: 0, 
                                                                mr: open ? 1.5 : 0, 
                                                                justifyContent: 'center',
                                                                color: isActive ? '#fff' : '#64748b',
                                                            }}>
                                                                {MuiIcon ? (
                                                                    <MuiIcon sx={{ fontSize: 20 }} />
                                                                ) : (
                                                                    <Assets src={getIconPath(menu)} height={"20px"} width={"20px"} />
                                                                )}
                                                            </ListItemIcon>
                                                            {open && (
                                                                <ListItemText 
                                                                    primary={menu?.pageName} 
                                                                    sx={{
                                                                        '& .MuiTypography-root': {
                                                                            fontSize: '13px',
                                                                            fontWeight: isActive ? 600 : 500,
                                                                            color: isActive ? '#fff' : '#1e293b',
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </ListItemButton>
                                                    </Link>
                                                </ListItem>
                                            </Tooltip>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}

                        {/* Logout Option */}
                        <Box sx={{ mt: 2 }}>
                            <Tooltip title={!open ? 'Logout' : ''} placement="right" arrow>
                                <ListItem disablePadding sx={{ display: "block" }}>
                                    <ListItemButton 
                                        sx={{ 
                                            minHeight: 38,
                                            borderRadius: '8px',
                                            px: 1.5,
                                            py: 0.75,
                                            justifyContent: open ? 'initial' : 'center',
                                            '&:hover': {
                                                backgroundColor: '#fef2f2',
                                            },
                                        }}
                                        onClick={handleSidebarLogout}
                                    >
                                        <ListItemIcon sx={{ 
                                            minWidth: 0, 
                                            mr: open ? 1.5 : 0, 
                                            justifyContent: 'center',
                                            color: '#ef4444',
                                        }}>
                                            <LogoutOutlinedIcon sx={{ fontSize: 20 }} />
                                        </ListItemIcon>
                                        {open && (
                                            <ListItemText 
                                                primary="Logout"
                                                sx={{
                                                    '& .MuiTypography-root': {
                                                        fontSize: '13px',
                                                        fontWeight: 500,
                                                        color: '#ef4444',
                                                    }
                                                }}
                                            />
                                        )}
                                    </ListItemButton>
                                </ListItem>
                            </Tooltip>
                        </Box>
                    </List>


                </Drawer >

                <Box component="main"
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                        marginTop: { xs: '56px', sm: '70px' },
                        p: {
                            xs: `16px 12px calc(${MOBILE_BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom) + 12px) 12px`,
                            sm: '20px 16px 16px 16px',
                            md: '24px 20px 20px 20px',
                        },
                        backgroundColor: "#f3f3f7",
                        minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 70px)' },
                        height: 'auto',
                        overflowX: 'hidden',
                        overflowY: 'auto',
                        borderTopLeftRadius: { xs: '16px', md: '20px' },
                        borderLeft: '1px solid #e2e8f0',
                        borderTop: '1px solid #e2e8f0',
                    }}>
                    {children}
                </Box>

                {/* Mobile Bottom Navigation */}
                <MobileBottomNav
                    onMoreClick={() => setMobileMenuOpen(true)}
                    groupedPages={groupedPages}
                    sectionOrder={SECTION_ORDER}
                />

                {/* Mobile Drawer Menu (More Options) */}
                <MobileDrawerMenu
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    groupedPages={groupedPages}
                    sectionOrder={SECTION_ORDER}
                    user={user}
                    onLogout={() => {
                        setMobileMenuOpen(false);
                        confirmDialogue({
                            title: `Are you sure you want to Log Out?`,
                            description: "This action will log you out of your account.",
                            confirmationText: (
                                <Typography sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px" } }}>
                                    Yes, Log Out
                                </Typography>
                            ),
                            cancellationText: (
                                <Typography sx={{ fontSize: { xs: "12px", sm: "14px", md: "16px" } }}>
                                    NO, Stay Logged In
                                </Typography>
                            ),
                            titleProps: { sx: { fontSize: "18px", fontWeight: 500, color: "#3E4094", padding: "5px 23px" } },
                        }).then(() => { handleLogout(); });
                    }}
                />
            </Box >

        </>
    )
}

export default Layout
